import {
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Flex,
  List,
  notification,
  Spin,
  Tooltip,
} from "antd";
import {
  InboxOutlined,
  LikeFilled,
  LikeOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import VirtualList, { ListRef } from "rc-virtual-list";

import {
  useNotes,
  useDismissNoteMutation,
  useToggleNoteLikeMutation,
} from "notes/queries";
import { INote } from "notes/models";
import "./NotesList.scss";
import React from "react";
import { DateTimeFormat } from "shared/constants";
import { Link } from "react-router-dom";
import { useBoundStore } from "store";
import variables from "../../../_variables.scss";
import { utilityService } from "shared/services";

dayjs.extend(utc);
dayjs.extend(relativeTime);

// Highlight @mentions in note content
const highlightMentions = (html: string): string => {
  if (!html) return "";
  // Match @mentions that aren't already wrapped in a span
  // This regex matches @followed by one or more word characters (including spaces for full names)
  return html.replace(
    /@([A-Za-z]+(?:\s+[A-Za-z]+)?)/g,
    '<span class="mention-highlight">@$1</span>'
  );
};

type Props = {
  title: string;
  height?: number;
  projectId?: string;
  allowDismiss?: boolean;
  showProjectName?: boolean;
  showCard?: boolean;
  refreshTrigger?: number;
  // Controlled mode props (when provided, component uses these instead of internal hook)
  data?: INote[];
  hasMore?: boolean;
  onLoadMore?: () => void;
  loading?: boolean;
  onRefetch?: () => void;
  // Add note callback (optional - shows Add Note button when provided)
  onAddNote?: () => void;
  // Limit mode props (for dashboard widget - shows fixed count with View All link)
  limit?: number;
  onViewAll?: () => void;
  // Legacy props (deprecated, kept for backward compatibility)
  externalLoading?: boolean;
  onLoadingChange?: (loading: boolean) => void;
};

export const NotesList = ({
  title,
  projectId,
  height = 1050,
  allowDismiss = true,
  showProjectName = true,
  showCard = false,
  refreshTrigger,
  // Controlled mode props
  data: controlledData,
  hasMore: controlledHasMore,
  onLoadMore: controlledLoadMore,
  loading: controlledLoading,
  onRefetch: controlledRefetch,
  // Add note callback
  onAddNote,
  // Limit mode props
  limit,
  onViewAll,
  // Legacy props
  externalLoading = false,
  onLoadingChange,
}: Props) => {
  // Determine if we're in controlled mode (parent provides data/functions) or uncontrolled mode (use internal hook)
  const isControlled = controlledData !== undefined || controlledLoadMore !== undefined;

  // Always call hook (React rules), but only use it in uncontrolled mode
  const {
    data: internalQueryData,
    error: internalError,
    isSuccess: internalSuccess,
    isLoading: internalLoading,
    fetchNextPage: internalFetchNextPage,
    hasNextPage: internalHasMore,
    refetch: internalRefetch,
  } = useNotes(projectId, { enabled: !isControlled });

  const internalData = internalQueryData?.pages.flatMap(p => p.items) ?? [];

  // Use controlled props if provided, otherwise use internal hook values
  const data = controlledData ?? internalData;
  const hasMore = controlledHasMore ?? internalHasMore;
  const loading = controlledLoading ?? internalLoading;
  const loadMore = controlledLoadMore ?? (() => internalFetchNextPage());
  const refetch = controlledRefetch ?? internalRefetch;
  const error = isControlled ? undefined : internalError;
  const success = isControlled ? (data !== undefined && !loading) : internalSuccess;

  const { tenant, user: currentUser } = useBoundStore();
  const { mutateAsync: dismissNote } = useDismissNoteMutation();
  const { mutate: toggleNoteLike } = useToggleNoteLikeMutation();
  const [api, notificationContextHolder] = notification.useNotification();
  const [scrollTop, setScrollTop] = useState<number>(0);
  const listRef = React.useRef<ListRef>(null);
  const truncateContentLength = 200;
  const [showMoreContent, setShowMoreContent] = useState<string[]>([]);
  const removeMoreContent = (id: string) => {
    setShowMoreContent((prev) => prev.filter((item) => item !== id));
  };

  // Use full height for VirtualList - card stretches to match sibling and list fills card
  // When limit is set, show only that many notes and use simple list (no virtual scroll)
  const isLimitedMode = limit !== undefined;
  const displayData = isLimitedMode && data ? data.slice(0, limit) : data;
  const showViewAll = isLimitedMode && data && data.length > (limit || 0);

  // Resolve avatar: use DB profilePictureUrl, fall back to Auth0 picture for current user, then initials
  const getAvatarUrl = (note: INote): string | undefined => {
    if (note?.author?.profilePictureUrl) return note.author.profilePictureUrl;
    if (note?.author?.id === currentUser?.id && currentUser?.picture) return currentUser.picture;
    return undefined;
  };

  // Names behind the like button's tooltip — the visible record of who has
  // acknowledged the note. The viewer sees themselves as "You".
  const MAX_TOOLTIP_LIKERS = 10;
  const renderLikersTooltip = (note: INote) => {
    const likes = note?.likes ?? [];
    if (likes.length === 0) return "Like this note";

    const names = likes.map((like) =>
      like.userId === currentUser?.id
        ? "You"
        : like.displayName ||
          [like.firstName, like.lastName].filter(Boolean).join(" ") ||
          "Unknown user"
    );
    const shown = names.slice(0, MAX_TOOLTIP_LIKERS);
    const remaining = names.length - shown.length;

    return (
      <div className='note-likers-tooltip'>
        {shown.map((name, index) => (
          <div key={`${name}-${index}`}>{name}</div>
        ))}
        {remaining > 0 && <div>and {remaining} more</div>}
      </div>
    );
  };

  const renderLikeButton = (note: INote) => {
    const liked = !!note?.likedByCurrentUser;
    const count = note?.likeCount ?? 0;

    return (
      <Tooltip title={renderLikersTooltip(note)}>
        <Button
          type='text'
          size='small'
          aria-label={liked ? "Unlike note" : "Like note"}
          aria-pressed={liked}
          className={`note-like-button${liked ? " note-like-button--liked" : ""}`}
          icon={liked ? <LikeFilled /> : <LikeOutlined />}
          onClick={() => {
            if (!note?.id) return;
            toggleNoteLike({ noteId: note.id, liked });
          }}
        >
          {count > 0 && <span className='note-like-count'>{count}</span>}
        </Button>
      </Tooltip>
    );
  };

  // Render a single note item (shared between limited and unlimited modes)
  const renderNoteItem = (note: INote) => {
    const showMore = showMoreContent.includes(note?.id ?? "");
    const avatarUrl = getAvatarUrl(note);

    return (
      <List.Item className='notes-list-item' key={note.id}>
        <Flex vertical className='note-item-body'>
          <List.Item.Meta
          avatar={
            <Avatar
              shape='circle'
              size='small'
              src={avatarUrl}
              icon={!avatarUrl && !note?.author?.firstName && !note?.author?.lastName ? <UserOutlined /> : undefined}
              style={!avatarUrl && (note?.author?.firstName || note?.author?.lastName) ? { backgroundColor: variables.primaryColor } : undefined}
            >
              {!avatarUrl && (note?.author?.firstName || note?.author?.lastName)
                ? `${note?.author?.firstName?.charAt(0) ?? ""}${note?.author?.lastName?.charAt(0) ?? ""}`.toUpperCase()
                : null}
            </Avatar>
          }
          title={
            <Flex justify='space-between' align='flex-start'>
              <Flex vertical>
                <span className='note-author-name'>
                  {note?.author?.displayName}
                </span>
                {showProjectName && note?.project?.name && (
                  <Link
                    to={`/${tenant?.id}/organizations/${note?.organizationId}/projects/${note?.project?.id}`}
                    className='note-project-link'
                  >
                    {utilityService.truncateField(
                      note?.project?.name,
                      30
                    )}
                  </Link>
                )}
              </Flex>
              {allowDismiss && (
                <Tooltip title='Archive note'>
                  <Button
                    type='text'
                    onClick={async () => {
                      if (!note?.id) return;
                      await dismissNote(note.id);
                    }}
                    size='small'
                    className='note-archive-button'
                  >
                    <InboxOutlined />
                  </Button>
                </Tooltip>
              )}
            </Flex>
          }
          description={
            <Flex vertical gap={variables.smallGap}>
              <span style={{ fontSize: 10 }}>
                {dayjs(note?.createdAt).format(DateTimeFormat)}
              </span>
              <div
                dangerouslySetInnerHTML={{
                  __html: highlightMentions(
                    (showMore
                      ? note?.content
                      : utilityService.truncateHtml(
                          note?.content ?? "",
                          truncateContentLength
                        )) as string
                  ),
                }}
              />
            </Flex>
          }
        />
        {/* Sibling of the meta, not part of its text column, so the like
            control lines up with the avatar rather than the note body. */}
        <Flex
          align='center'
          gap={variables.smallGap}
          className='note-actions'
        >
          {renderLikeButton(note)}
          {(note?.content?.length ?? 0) >
            truncateContentLength &&
            !showMore && (
              <Button
                type='text'
                size='small'
                className='note-show-more-button'
                onClick={() =>
                  setShowMoreContent([...showMoreContent, note?.id ?? ""])
                }
              >
                <b>Show more</b>
              </Button>
            )}
          {(note?.content?.length ?? 0) > truncateContentLength && showMore && (
            <Button
              type='text'
              size='small'
              className='note-show-more-button'
              onClick={() => removeMoreContent(note?.id ?? "")}
            >
              <b>Show less</b>
            </Button>
          )}
          </Flex>
        </Flex>
      </List.Item>
    );
  };

  const Content = () => (
    <Flex data-testid='notesList' vertical gap={variables.gap} className='notes-list-content'>
      {notificationContextHolder}
      {(title || onAddNote) && (
        <Flex align='center' justify='space-between'>
          <Flex align='center' gap={variables.smallGap}>
            {title && <h1 style={{ marginBottom: 0 }}>{title}</h1>}
            {title && !isLimitedMode && data?.length > 0 && (
              <Badge count={data.length} color={variables.primaryColor} />
            )}
          </Flex>
          {onAddNote && (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={onAddNote}
              size='small'
            >
              Add Note
            </Button>
          )}
        </Flex>
      )}
      {displayData?.length > 0 ? (
        success ? (
          <>
            {isLimitedMode ? (
              // Limited mode: simple list without virtual scroll
              <List
                itemLayout='horizontal'
                dataSource={displayData}
                renderItem={renderNoteItem}
                className='notes-list-container'
              />
            ) : (
              // Unlimited mode: virtual scroll for performance
              <List itemLayout='horizontal'>
                <VirtualList
                  ref={listRef}
                  data={displayData}
                  className='notes-list-container'
                  height={height}
                  itemKey='id'
                  onScroll={async (e: React.UIEvent<HTMLElement, UIEvent>) => {
                    if (
                      e.currentTarget.scrollHeight - e.currentTarget.scrollTop <=
                        e.currentTarget.clientHeight + 25 &&
                      hasMore &&
                      !loading &&
                      loadMore
                    ) {
                      setScrollTop(e.currentTarget.scrollHeight);
                      loadMore();
                    }
                  }}
                >
                  {renderNoteItem}
                </VirtualList>
              </List>
            )}
            {showViewAll && onViewAll && (
              <Button
                type='link'
                onClick={onViewAll}
                className='notes-view-all-link'
              >
                View All Notes
              </Button>
            )}
          </>
        ) : loading ? (
          <Spin size='large' className='note-spin' />
        ) : (
          <Empty />
        )
      ) : (
        <Empty />
      )}
    </Flex>
  );

  // Handle refresh trigger - use controlled refetch if provided, otherwise use internal
  useEffect(() => {
    if (refreshTrigger && refetch) {
      refetch();
    }
  }, [refreshTrigger, refetch]);

  useEffect(() => {
    listRef?.current?.scrollTo({
      index: scrollTop,
      align: "top",
      offset: 15,
    });
  }, [data]);

  // Legacy: notify parent of loading state changes (only in uncontrolled mode)
  useEffect(() => {
    if (!isControlled && onLoadingChange) {
      onLoadingChange(!success);
    }
  }, [isControlled, success, onLoadingChange]);

  useEffect(() => {
    if (error) {
      api.error({
        message: "Error",
        description: error?.message,
      });
    }
  }, [error, api]);

  return (
    <>
      {showCard ? (
        <Card className='notes-card' loading={externalLoading ? false : (loading || !success)} bordered={false}>
          <Content />
        </Card>
      ) : (
        <Content />
      )}
    </>
  );
};
